import { HttpError, useOne, useTranslation, useUpdate } from "@refinedev/core";
import { TrashIcon } from "@heroicons/react/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { get, isEmpty } from "lodash";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { ParticipantFormSchema } from "components/attendantTemplate/schema";
import Button from "components/button";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
// import { endpointService } from "components/shared/services/endpointService";
import {
  FieldObjectType,
  IAttendantTemplateInputItem,
  InputType,
  IPickupAttractionParticipantForm,
  IPickupSportParticipantForm,
} from "interfaces";
import { isHaveActionPermission } from "utills/helpers";
import { getConfig } from "../config";
import { getListLabels } from "../labels";
import {
  EditSections,
  ParticipantDetail,
  PickupParticipantKind,
  PickupParticipantVariant,
} from "../types";
import { getMessage } from "../utils";

export const PickupParticipantEditPage = ({
  kind,
  variant,
  sections,
}: {
  kind: PickupParticipantKind;
  variant: PickupParticipantVariant;
  sections: EditSections;
}) => {
  const config = getConfig(kind, variant);
  const { translate, getLocale } = useTranslation();
  const language = getLocale();
  const labels = getListLabels(kind, variant, translate);
  const { id } = useParams();
  const navigate = useNavigate();
  const { mutate, isLoading: isUpdating } = useUpdate();
  const { data, isFetching } = useOne<ParticipantDetail>({
    resource: config.detailResource,
    id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!id,
    } as any,
  });
  const { data: groupData, isFetching: isFetchingGroups } = useOne<any[], HttpError>({
    resource: "content/dropdown/clubs?dict_response=true",
    id: "",
    meta: {
      resource: "data",
    },
  });

  const participantDetailData = data?.data ?? null;
  const participantId = get(participantDetailData, "participant.participant_id", id ?? "");
  const groups = groupData?.data ?? null;

  const { data: posData, refetch: refetchCheckPosData } = useOne<{
    pos_id: string;
    pos_name: string;
    pos_username: string;
  }>({
    resource: config.detailResource,
    id: `${participantId}/check-pos`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: kind === "sport" && !!participantDetailData,
    } as any,
  });

  const removePosCheckin = (posId: string) => {
    mutate(
      {
        resource: config.detailResource,
        id: `${participantId}/${posId}`,
        meta: {
          URLSuffix: "clear",
          ignoreResourceWrapper: true,
        },
        values: {},
      },
      {
        onSuccess: (res) => {
          toast.success(getMessage(get(res, "data.message"), language, labels.removePos));
          refetchCheckPosData();
        },
      },
    );
  };

  const submitForm = async (values: IPickupSportParticipantForm | IPickupAttractionParticipantForm) => {
    mutate(
      {
        resource: config.detailResource,
        id: participantId,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: variant === "wps" ? values : { data: { ...values } },
      },
      {
        onSuccess: (res) => {
          toast.success(getMessage(get(res, "data.message"), language, "Edit successfully"));
          navigate(config.detailPath(participantId));
        },
      },
    );
  };

  if (isFetching || isFetchingGroups) return <FullScreenLoading />;

  return (
    <div className={variant === "wps" ? "w-full" : ""}>
      <Breadcrumbs
        type={variant === "wps" ? "wps" : undefined}
        customPageLabel={variant === "admin" ? labels.editTitle : undefined}
        breadcrumbItems={
          variant === "wps"
            ? [
                { label: labels.checkIn },
                { label: labels.pageTitle, href: config.basePath },
                { label: labels.editTitle, href: config.editPath(participantId) },
              ]
            : undefined
        }
        disableBorder={variant === "wps" ? true : undefined}
        additionalLabel={
          kind === "sport" &&
          participantDetailData &&
          !isEmpty(posData?.data) &&
          isHaveActionPermission(config.permissionResource, config.permissionApp, "delete") ? (
            <Button
              className="w-11 h-11 sm:w-auto bg-brand-secondary-500 border-brand-secondary-500 border-[1px] border-solid text-white hover:bg-brand-secondary-500 active:bg-brand-secondary-500 active:opacity-85"
              onClick={() => removePosCheckin(posData?.data?.pos_id ?? "")}
            >
              <span className="sr-only">{labels.removePos}</span>
              <TrashIcon className="size-4 text-white" />{" "}
              <span className="hidden sm:block sm:ml-2">{labels.removePos}</span>
            </Button>
          ) : null
        }
      />
      {participantDetailData ? (
        <ParticipantForm
          kind={kind}
          variant={variant}
          labels={labels}
          groups={groups}
          participantDetailData={participantDetailData}
          sections={sections}
          onSubmit={submitForm}
        />
      ) : null}
      {isUpdating ? <FullScreenLoading /> : null}
    </div>
  );
};

const getTemplateDefaultValues = (
  participantDetailData: ParticipantDetail,
  attendantTemplate: ParticipantDetail["attendant_template"],
) => {
  const result: Record<string, any> = {};

  for (const section in attendantTemplate ?? {}) {
    const fields = attendantTemplate?.[section]?.fields ?? [];

    fields.forEach((field: IAttendantTemplateInputItem) => {
      if (!field.key) return;

      const participantValue = get(participantDetailData, `participant.${field.key}`, "");

      if (field.type === InputType.SELECT) {
        result[field.key] = (field?.option ?? {}).hasOwnProperty(participantValue)
          ? participantValue
          : "";
      } else if (field.type === InputType.MULTI_SELECT || field.type === InputType.RADIO_BUTTON) {
        const selectedValues = String(participantValue ?? "")
          .split(",")
          .filter(Boolean);
        const validOption: string[] = [];

        (field?.option ?? []).forEach((opt: { Key: string; Value: any }[]) => {
          const optionValue = get(opt[0], "Value", "");
          if (selectedValues.includes(optionValue)) {
            validOption.push(optionValue);
          }
        });

        result[field.key] = validOption.join(",");
      } else {
        result[field.key] = participantValue ?? "";
      }

      if (field.key.includes("phone") && field.validation === "phone") {
        result[`${field.key}_area_code`] = get(
          participantDetailData,
          `participant.${field.key}_area_code`,
          "",
        );
      }
    });
  }

  return result;
};

const ParticipantForm = ({
  kind,
  variant,
  labels,
  groups,
  participantDetailData,
  sections,
  onSubmit,
}: {
  kind: PickupParticipantKind;
  variant: PickupParticipantVariant;
  labels: ReturnType<typeof getListLabels>;
  groups: any;
  participantDetailData: ParticipantDetail;
  sections: EditSections;
  onSubmit: (data: any) => Promise<void>;
}) => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const isSport = kind === "sport";
  const templateData = useMemo(() => {
    const formattedData: any = {};

    for (let groupKey in participantDetailData.attendant_template ?? {}) {
      const group = participantDetailData.attendant_template[groupKey];
      formattedData[groupKey] = {
        ...group,
        fields: group.fields.map((field: IAttendantTemplateInputItem) =>
          field.object === FieldObjectType.CLUBS ? { ...field, option: groups } : field,
        ),
      };
    }

    return {
      data: {
        ...formattedData,
      },
    };
  }, [groups, participantDetailData.attendant_template]);
  const isFetchingTemplate = false;

  // const eventId = participantDetailData?.event?.id;
  // const attendantTemplateId = participantDetailData?.participant?.attendant_template_id;
  // const templateResource = endpointService.getEventEndpoint("getParticipantAttendantTemplate", {
  //   eventId: eventId ?? "",
  //   templateId: attendantTemplateId ?? "",
  // });
  // const { data: templateData, isFetching: isFetchingTemplate } = useOne<any[], HttpError>({
  //   resource: templateResource,
  //   id: "",
  //   meta: {
  //     resource: "data",
  //   },
  //   queryOptions: {
  //     enabled: !!(attendantTemplateId && eventId),
  //     select: (response: any) => {
  //       const formattedData: any = {};
  //
  //       for (let groupKey in response.data) {
  //         const group = response.data[groupKey];
  //         formattedData[groupKey] = {
  //           ...group,
  //           fields: group.fields.map((field: IAttendantTemplateInputItem) =>
  //             field.object === FieldObjectType.CLUBS ? { ...field, option: groups } : field,
  //           ),
  //         };
  //       }
  //
  //       return {
  //         ...response,
  //         data: {
  //           ...formattedData,
  //         },
  //       };
  //     },
  //   } as any,
  // });
  const attendantTemplate = templateData?.data ?? {};
  const initialValues = useMemo(() => {
    const baseValues = isSport
      ? {
          category: participantDetailData.participant?.category ?? "",
          bib: get(participantDetailData, "participant.bib", ""),
          full_name: participantDetailData.participant.full_name ?? "",
          self_pick: get(participantDetailData, "participant.self_pick", false),
          name_pick: get(participantDetailData, "participant.name_pick", ""),
          email_pick: get(participantDetailData, "participant.email_pick", ""),
          phone_pick: get(participantDetailData, "participant.phone_pick", ""),
          id_card_pick: get(participantDetailData, "participant.id_card_pick", ""),
        }
      : {
          category: participantDetailData.participant?.category ?? "",
          full_name: participantDetailData.participant.full_name ?? "",
        };

    return {
      ...baseValues,
      ...getTemplateDefaultValues(participantDetailData, attendantTemplate),
    };
  }, [attendantTemplate, isSport, participantDetailData]);
  const informationSchema = isSport
    ? z.object({
        bib: z.string().optional(),
        full_name: z.string().optional(),
        category: z.string().min(1, variant === "wps" ? translate("validation.errorMessage.thisFieldIsRequired") : "This field is required"),
        email_pick: z.string().optional(),
        name_pick: z.string().optional(),
        id_card_pick: z.string().optional(),
        phone_pick: z.string().optional(),
        self_pick: z.boolean(),
      })
    : z.object({
        full_name: z.string().optional(),
        category: z.string().min(1, variant === "wps" ? translate("validation.errorMessage.thisFieldIsRequired") : "This field is required"),
      });

  const methods = useForm<any>({
    defaultValues: initialValues,
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver: any = zodResolver(
        z.object({
          ...informationSchema.shape,
          ...ParticipantFormSchema(
            attendantTemplate,
            get(values, "dob", null),
            get(participantDetailData, "event.start_date", "") || "",
          ).shape,
        }),
      );
      return createResolver(values, context, options);
    },
  });

  useEffect(() => {
    methods.reset(initialValues);
  }, [initialValues, methods]);

  const { InformationSection, RegistrationInformationSection, PickupInformationSection } = sections;

  return (
    <div>
      {isFetchingTemplate ? <FullScreenLoading /> : null}
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit, (errors) => {
            const firstErrorKey = Object.keys(errors)[0];
            if (firstErrorKey) methods.setFocus(firstErrorKey as any);
          })}
          className={variant === "wps" ? "w-full p-[16px] flex flex-col gap-[16px]" : "w-full flex flex-col items-center gap-y-3 sm:gap-y-4 h-full"}
        >
          <InformationSection participantDetailData={participantDetailData as any} />
          <RegistrationInformationSection participantDetailData={participantDetailData as any} template={attendantTemplate} />
          {PickupInformationSection ? <PickupInformationSection participantDetailData={participantDetailData as any} /> : null}
          <div
            className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto"
            style={{ boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px" }}
          >
            <div className="flex items-center justify-end gap-x-4">
              <Button
                type="button"
                onClick={() => navigate(-1)}
                className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
              >
                {labels.cancel}
              </Button>
              <Button className="w-20" type="submit">
                {labels.save}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
